const HEX_COLOR_REGEX = /^#[0-9a-f]{6}$/i;
const ALL_COLOR_STOPS_REGEX =
    /#[0-9a-f]{6}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*\d*\.?\d+\s*)?\)/gi;
const RGB_CHANNELS_REGEX = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i;
const MAX_CHANNEL = 255;
const GRADIENT_DIRECTION = "to right";
const LIGHT_FOREGROUND = "#FFFFFF";
const DARK_FOREGROUND = "#262626";
const WCAG_LUMINANCE_OFFSET = 0.05;
const SRGB_LINEAR_THRESHOLD = 0.04045;
const SRGB_GAMMA_THRESHOLD = 0.0031308;
const LIGHT_COLOR_THRESHOLD = 0.72;
const HOVER_COLOR_RATIO = 0.86;
const HOVER_MIN_LIGHTNESS = 0.3;
const WHITE_OKLAB = { lightness: 1, a: 0, b: 0 };
const BLACK_OKLAB = { lightness: 0, a: 0, b: 0 };

function toLinearRgb(hex) {
    return [1, 3, 5]
        .map((offset) => parseInt(hex.substr(offset, 2), 16) / 255)
        .map((value) =>
            value <= SRGB_LINEAR_THRESHOLD
                ? value / 12.92
                : Math.pow((value + 0.055) / 1.055, 2.4)
        );
}

function relativeLuminance(hex) {
    const [red, green, blue] = toLinearRgb(hex);

    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function contrastRatio(first, second) {
    const [lighter, darker] = [relativeLuminance(first), relativeLuminance(second)].sort(
        (a, b) => b - a
    );

    return (lighter + WCAG_LUMINANCE_OFFSET) / (darker + WCAG_LUMINANCE_OFFSET);
}

function stopToHex(stop) {
    if (HEX_COLOR_REGEX.test(stop)) return stop.toUpperCase();

    const channels = (stop.match(RGB_CHANNELS_REGEX) || []).slice(1).map(Number);

    if (channels.length !== 3) return null;

    if (channels.some((channel) => channel > MAX_CHANNEL)) return null;

    const digits = channels
        .map((channel) => channel.toString(16).padStart(2, "0"))
        .join("");

    return `#${digits.toUpperCase()}`;
}

function toOklab(hex) {
    const [red, green, blue] = toLinearRgb(hex);

    const long = Math.cbrt(
        0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue
    );
    const medium = Math.cbrt(
        0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue
    );
    const short = Math.cbrt(
        0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue
    );

    return {
        lightness: 0.2104542553 * long + 0.793617785 * medium - 0.0040720468 * short,
        a: 1.9779984951 * long - 2.428592205 * medium + 0.4505937099 * short,
        b: 0.0259040371 * long + 0.7827717662 * medium - 0.808675766 * short,
    };
}

function mixOklab(color, target) {
    const rest = 1 - HOVER_COLOR_RATIO;

    return {
        lightness: HOVER_COLOR_RATIO * color.lightness + rest * target.lightness,
        a: HOVER_COLOR_RATIO * color.a + rest * target.a,
        b: HOVER_COLOR_RATIO * color.b + rest * target.b,
    };
}

function oklabToHex({ lightness, a, b }) {
    const long = Math.pow(lightness + 0.3963377774 * a + 0.2158037573 * b, 3);
    const medium = Math.pow(lightness - 0.1055613458 * a - 0.0638541728 * b, 3);
    const short = Math.pow(lightness - 0.0894841775 * a - 1.291485548 * b, 3);

    const digits = [
        4.0767416621 * long - 3.3077115913 * medium + 0.2309699292 * short,
        -1.2684380046 * long + 2.6097574011 * medium - 0.3413193965 * short,
        -0.0041960863 * long - 0.7034186147 * medium + 1.707614701 * short,
    ]
        .map((channel) => {
            const gamma =
                channel <= SRGB_GAMMA_THRESHOLD
                    ? 12.92 * channel
                    : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;

            return Math.round(Math.min(255, Math.max(0, gamma * 255)))
                .toString(16)
                .padStart(2, "0");
        })
        .join("");

    return `#${digits.toUpperCase()}`;
}

export default {
    isHex(color) {
        return !!color && HEX_COLOR_REGEX.test(color);
    },

    parseColorStops(value) {
        const matches = (value && value.match(ALL_COLOR_STOPS_REGEX)) || [];

        return matches.map(stopToHex).filter(Boolean);
    },

    parseGradient(value) {
        const stops = this.parseColorStops(value);

        const start = stops[0] || null;
        const end = stops[stops.length - 1] || start;

        return { start, end };
    },

    buildGradient(start, end) {
        return `linear-gradient(${GRADIENT_DIRECTION}, ${start}, ${end})`;
    },

    lighten(color, amount) {
        if (!this.isHex(color)) return color;

        const channels = [1, 3, 5].map((offset) =>
            parseInt(color.substr(offset, 2), 16)
        );

        const mixed = channels.map((channel) =>
            Math.round(channel + (255 - channel) * amount)
        );

        return `rgb(${mixed.join(", ")})`;
    },

    deriveHover(color) {
        if (!this.isHex(color)) return color;

        const oklab = toOklab(color);

        const isLightening = oklab.lightness <= LIGHT_COLOR_THRESHOLD;

        const mixed = mixOklab(oklab, isLightening ? WHITE_OKLAB : BLACK_OKLAB);

        if (isLightening) {
            mixed.lightness = Math.max(mixed.lightness, HOVER_MIN_LIGHTNESS);
        }

        return oklabToHex(mixed);
    },

    foregroundFor(color) {
        if (!this.isHex(color)) return LIGHT_FOREGROUND;

        const hover = this.deriveHover(color);

        const worstRatio = (foreground) =>
            Math.min(contrastRatio(color, foreground), contrastRatio(hover, foreground));

        return worstRatio(DARK_FOREGROUND) > worstRatio(LIGHT_FOREGROUND)
            ? DARK_FOREGROUND
            : LIGHT_FOREGROUND;
    },

    contrastColor(color) {
        if (!this.isHex(color)) return LIGHT_FOREGROUND;

        return contrastRatio(color, DARK_FOREGROUND) > contrastRatio(color, LIGHT_FOREGROUND)
            ? DARK_FOREGROUND
            : LIGHT_FOREGROUND;
    },
};
